import React from 'react'
import * as yup from 'yup';
import extend from 'extend';
import { withRouter } from 'react-router-dom';

import BrandLogo from '@components/BrandLogo/BrandLogo'
import LayoutCentered from '@components/LayoutCentered/LayoutCentered'
import Wizard from '@components/Wizard/Wizard'
import RegistrationComplete from './RegistrationComplete';
import { FormGroup, FieldGroup } from '@modules/form/form.module'

import { cleanData } from '@utils/data'
import { CandidateModel } from '@modules/user/user.module'

import './Register.scss';

const schemaOptions = { isNew: true, includePrivate: true, strictMode: true };

const dateFormatLabel = '(dd/mm/yyyy)';

const getStep1 = () => {
	const readAndAgreed = { label: '', value: 'I have read and acknowledged this information' };

	CandidateModel.startFieldBuffer();

	const formGroups = [
		(<FormGroup key="data-protection" label="Data Protection">
			<h3>Part 1</h3>
			<p>I acknowledge and consent that Human One may collect and process personal information and data about me. I understand that all of my information (including my personal data) is collected and processed in accordance with the Data Protection Act 1998, and the General Data Protection Regulations 2016. I agree and consent to the personal information that I provide being processed by Human One, and the relevant 3rd party, to carry out a basic disclosure check on my behalf.</p>
			<FieldGroup {...CandidateModel.getFieldProps(['dataProtectionAgreementPart1', readAndAgreed])} />
			<h3>Part 2</h3>
			<p>I acknowledge that the information I provide on this form and on any CV given will be used by Human One to provide me work finding services. In providing this service to me, I consent to my personal data being included on a computerised database and consent to Human One transferring my personal details to their clients. Human One may check the information collected, with third parties or with other information held by them.</p>
			<p>Human One may also use or pass to certain third parties information to prevent or detect crime, to protect public funds, or in other way permitted or required by law.</p>
			<FieldGroup {...CandidateModel.getFieldProps(['dataProtectionAgreementPart2', readAndAgreed])} />
		</FormGroup>)
	];

	const fieldPaths = CandidateModel.endFieldBuffer();
	const validationSchema = CandidateModel.getValidationSchema(fieldPaths, schemaOptions);

	return (
		<Wizard.Page key="step1" validationSchema={validationSchema}>
			{formGroups}
		</Wizard.Page>
	);
}

const getStep2 = () => {
	CandidateModel.startFieldBuffer();
	const groups = [
		{
			label: 'About you',
			fields: CandidateModel.getFieldProps([
				'jobsApplyingFor',
				['name.title', {
					label: 'Your title'
				}],
				['name.first', {
					label: 'Your first name'
				}],
				['name.middle', {
					label: 'Your middle name (optional)'
				}],
				['name.last', {
					label: 'Your last name'
				}],
				['dateOfBirth', {
					label: 'Your date of birth ' + dateFormatLabel,
				}],
				['gender', {
					label: "Are you male or female?"
				}],
			]),
		},
		{
			label: 'Your contact details',
			fields: CandidateModel.getFieldProps([
				['mobile', {
					label: "Your mobile number",
				}],
				['telephone', {
					label: "Your home telephone number"
				}],
				['email', {
					label: "Your email address"
				}],
			]),
		},
		{
			label: 'Emergency contact details',
			fields: CandidateModel.getFieldProps([
				'emergencyContact.name',
				['emergencyContact.relationship', {
					label: 'Their relationship to you',
				}],
				['emergencyContact.telephone', {
					label: 'Their home telephone number (optional)',
				}],
				['emergencyContact.mobile', {
					label: 'Their mobile number',
				}],
			])
		},
		{
			label: 'Right to Work and Pre-employment screening',
			text: "Some of the below information is required for a basic disclosure check to take place",
			fields: CandidateModel.getFieldProps([
				// Immigration permission
				'immigrationPermission',
				// National identity card
				'nationalIdentityCardAvailable',
				['nationalIdentityCardNumber', {
					label: 'Identity card number'
				}],
				'nationalIdentityCardExpiry',
				// Passport
				'passportAvailable',
				['passportID', {
					label: 'Your passport ID'
				}],
				'passportExpiry',
				// Visa
				'visaAvailable',
				['visaID', {
					label: 'Your visa ID'
				}],
				'visaExpiry',
				// BRP
				'BRPAvailable',
				['BRPID', {
					label: 'Your BRP ID'
				}],
				'BRPExpiry',
				// UK Birth Certificate
				'birthCertificateAvailable',
				['birthCertificateID', {
					label: 'Your UK Birth Certificate ID'
				}],
				['nationality', {
					label: 'Your nationality'
				}],
				['countryOfBirth', {
					label: 'Your country of birth'
				}],
				['townCityOfBirth', {
					label: 'Your town/city of birth'
				}],
				['regionOfBirth', {
					label: 'Your region / county of birth'
				}],
				['mothersMaidenName', {
					label: 'Your mother\'s maiden name',
				}],
				['nationalityChangedSinceBirth', {
					label: 'Has your nationality changed since birth?'
				}],
				['birthNationality', {
					label: 'Your previous nationality'
				}],
				['dateNationalityChanged', {
					label: 'Date your nationality changed?'
				}],
				['surnameChangedSinceBirth', {
					label: 'Has your surname changed since birth?'
				}],
				['previousSurname', {
					label: 'Your previous surname'
				}],
				['dateSurnameChanged', {
					label: 'Date your surname changed?'
				}],
				['drivingLicenceAvailable', {
					label: 'Driving licence available?'
				}],
				['drivingLicenceNumber', {
					label: 'Driving licence number'
				}],
				'SIALicenceAvailable',
				'SIALicenceType',
				'foodSafetyCertificateAvailable',
				'foodSafetyCertificateLevel',
				// unspent convictions
				['hasUnspentConvictions', {
					label: 'Do you have any unspent criminal convictions?',
				}],
				['convictionsDescription', {
					label: ''
				}],
			])
		},
		{
			label: 'Your address',
			text: 'Previous 5 years of residency required',
			fields: CandidateModel.getFieldProps([
				'addressHistory',
			])
		},
		{
			label: 'Bank details',
			fields: CandidateModel.getFieldProps([
				['paymentDetails.available', {
					label: 'Do you have a bank account available?',
				}],
				'paymentDetails.bankName',
				'paymentDetails.bankNameCustom',
				'paymentDetails.accountHolderName',
				'paymentDetails.sortCode',
				'paymentDetails.accountNumber',
			])
		},
	];
	const fieldPaths = CandidateModel.endFieldBuffer();
	const validationSchema = CandidateModel.getValidationSchema(fieldPaths, schemaOptions);

	return (
		<Wizard.Page key="step2" validationSchema={validationSchema}>
			{groups.map((group, i) =>
				<FormGroup {...group} key={i} />
			)}
		</Wizard.Page>
	)
}

const getStep3 = () => {

	CandidateModel.startFieldBuffer();
	const groups = [
		{
			label: 'Employment History',
			text: 'One previous job required',
			fields: CandidateModel.getFieldProps([
				'employmentHistory',
			]),
		},
		{
			label: 'Referee Consent',
			fields: CandidateModel.getFieldProps([
				['refereeConsent', {
					label: 'Please note that it is our policy to obtain references from your previous employer, school, college or university',
					value: 'I hereby give Human One my permission to contact the above named referees',
				}]
			])
		},
		{
			label: 'Employment Scheme',
			fields: CandidateModel.getFieldProps([
				['employmentScheme', {
					label: 'Which type of employment contract do you require?',
				}],
				['UTRNumber', {
					label: "Please provide your UTR number"
				}],
				['NINumberAvailable', {
					label: 'Do you have a national insurance number?',
				}],
				['NINumber', {
					label: 'Your national insurance number'
				}],
			]),
		},
	];
	const fieldPaths = CandidateModel.endFieldBuffer();

	console.log(fieldPaths);
	const validationSchema = CandidateModel.getValidationSchema(fieldPaths, schemaOptions);

	return (
		<Wizard.Page key="step3" validationSchema={validationSchema}>
			{groups.map((group, i) =>
				<FormGroup {...group} key={i} />
			)}
		</Wizard.Page>
	)
}

const getStep4 = () => {
	CandidateModel.startFieldBuffer();
	const groups = [
		{
			label: 'Previous agencies',
			fields: CandidateModel.getFieldProps([
				['previousAgencies', {
					label: 'What other agencies have you worked with and where did they send you?',
				}],
			]),
		},
		{
			label: 'Working hours',
			fields: CandidateModel.getFieldProps([
				['availability', {
					label: 'What hours and days are available to work?\n(Please leave days you cannot work blank.)',
				}],
			]),
		},
	];
	const healthFields = CandidateModel.getFieldProps([
		['hasHealthIssues', {
			label: 'Do you have any health issues of disability relevant which make it difficult for you to carry out functions which are essential for the role you seek?'
		}],
		['healthIssuesDescription', {
			label: 'Please detail',
		}],
		['assistanceRequired', {
			label: 'What are your needs in terms of reasonable adjustments in order to access this recruitment service and to attend interview, or to take aptitude tests etc? Please detail.'
		}],
	]);

	const workDeclaration = CandidateModel.getFieldProps([
		['optOut48HourWorkingWeek', {
		}],
		['agreementTo48HourWorkingWeek', {
		}]
	])

	const fieldPaths = CandidateModel.endFieldBuffer();
	const validationSchema = CandidateModel.getValidationSchema(fieldPaths, schemaOptions);

	return (
		<Wizard.Page key="step4" validationSchema={validationSchema}>
			{groups.map((group, i) =>
				<FormGroup {...group} key={i} />
			)}
			<FormGroup key="working-week-agreement" label="Working Time Regulations - 48 Hour Week Opt-Out">
				<div>
					<p>The Working Time Regulations 1998 (as amended), effective from 1 October 1998, state that a worker's average working time shall not exceed 48 hours per week on average over a 17 week period (or other reference period as agreed), unless the worker agrees in writing that the limit shall not apply in their case.</p>
				</div>

				{workDeclaration.map(props =>
					<FieldGroup key={props.name} {...props}/>
				)}

			</FormGroup>
			<FormGroup key="health-and-disability" label="Health & Disability">
				<div>
					<p>The following questions on health and disability are asked in order to find out your needs in terms of reasonable adjustment to access our recruitment service and to find out your needs in order to perform the job or position sought.</p>
				</div>
				{
					healthFields.map(props =>
						<FieldGroup key={props.name} {...props} />
					)
				}
			</FormGroup>
		</Wizard.Page>
	)
}

const getStep5 = () => {
	const readAndAgreed = { label: '', value: 'I have read and acknowledged this information' };

	CandidateModel.startFieldBuffer();

	const formGroups = [
		(<FormGroup key="equal-opportunities" label="Equal Opportunities">
			<div>
				<p>Human One is committed to a policy of equal opportunities for all work seekers and shall adhere to such a policy at all times and will review on an on-going basis all aspects of recruitment to avoid unlawful or undesirable discrimination.</p>
				<p>We will treat everyone equally irrespective sex, sexual orientation, gender reassignment, marital or civil partnership status, age, disability, colour, race, nationality, ethnic or national origin, religion or belief, political beliefs or membership or non-membership of a Trade Union and we place an obligation upon all staff to respect and act in accordance with the policy.</p>
				<p>Human One shall not discriminate unlawfully when deciding which candidate/temporary worker is submitted for a vacancy or assignment, or in any terms of employment or terms of engagement for temporary workers. Human One will ensure that each candidate is assessed only in accordance with the candidate’s merits, qualification and ability to perform the relevant duties required by the particular vacancy.</p>
			</div>
			<FieldGroup {...CandidateModel.getFieldProps(['equalOpportunitiesAgreement', readAndAgreed])} />
		</FormGroup>),
		(<FormGroup key="candidate-declaration" label="Candidate Declaration">
			<p>I hereby confirm that the information given is true and correct. I consent to my personal data and CV being forwarded to clients. I consent to references being passed onto potential employers.</p>
			<p>If, during the course of a temporary assignment, the Client wishes to employ me direct, I acknowledge that Human One will be entitled to charge the client an introduction/transfer fee.	</p>
			<p>I have read and understood this statement.</p>
			<FieldGroup {...CandidateModel.getFieldProps(['candidateSigned', { label: 'Signed:' }])} />
		</FormGroup>)
	];

	const accountGroup = {
		label: '',
		fields: CandidateModel.getFieldProps([
			['password', {
				label: 'Set an account password',
			}],
			'passwordConfirm',
			'heardAboutHumanOne',
		])
	};

	const fieldPaths = CandidateModel.endFieldBuffer();
	const validationSchema = CandidateModel.getValidationSchema(fieldPaths, schemaOptions);

	return (
		<Wizard.Page key="step5" validationSchema={validationSchema}>
			{formGroups}
			<FormGroup {...accountGroup} />
		</Wizard.Page>
	)
}

@LayoutCentered
@withRouter
class Register extends React.Component {

	static stepCreators = [
		getStep1,
		getStep2,
		getStep3,
		getStep4,
		getStep5
	];

	constructor(props) {
		super(props);
		this.state = {
			completed: false,
			steps: this.constructor.stepCreators.map(f => f(props)),
		}
	}

	handleSubmit = (values, { props, setSubmitting }) => {
		fetch('/api/register', {
			method: 'post',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(cleanData(values)),
		})
			.then(res => {
				if (!res.ok) {
					alert('Something went wrong, please try again');
					setSubmitting(false);
					return false;
				}
				this.setState({ completed: true })
			});
	}

	render = () => (
		<div className="Register">
			{
				this.state.completed
					? <RegistrationComplete />
					: this.renderWizard()
			}
		</div>
	)

	renderWizard = () => (
		<div>
			<BrandLogo />
			<Wizard
				title="Register Now"
				initialValues={CandidateModel.getDefaults()}
				onSubmit={this.handleSubmit}
			>
				{this.state.steps}
			</Wizard>
		</div>
	)
}

export default Register



// WEBPACK FOOTER //
// ./src/scenes/Register/Register.jsx